import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { setSession, homeForRole } from "@/lib/auth";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Card } from "@/components/ui/card";
import { ArrowRight, Eye, EyeOff } from "lucide-react";

export default function Login() {
  const navigate = useNavigate();
  // Default the toggle to "Login ID" — most operational users log in with their assigned COKE9-XXXX id.
  const [mode, setMode] = useState("id");  // "phone" | "id"
  const [phone, setPhone] = useState("");
  const [loginId, setLoginId] = useState("");
  const [password, setPassword] = useState("");
  const [showPw, setShowPw] = useState(false);
  const [loading, setLoading] = useState(false);

  const submit = async (e) => {
    e?.preventDefault();
    if (mode === "phone" && !/^\d{10}$/.test(phone)) return toast.error("Enter a valid 10-digit phone");
    if (mode === "id" && (!loginId || loginId.length < 4)) return toast.error("Login ID must be at least 4 characters");
    if (!password || password.length < 6) return toast.error("Password must be at least 6 characters");
    setLoading(true);
    try {
      const payload = mode === "phone" ? { phone, password } : { login_id: loginId, password };
      const { data } = await api.post("/auth/login", payload);
      setSession(data.token, data.user);
      toast.success(`Welcome, ${data.user.name}`);
      navigate(homeForRole(data.user.role));
    } catch (err) {
      toast.error(err.response?.data?.detail || "Login failed");
    } finally { setLoading(false); }
  };

  return (
    <div className="min-h-screen flex flex-col md:flex-row">
      <div className="header-dark md:w-1/2 p-8 md:p-14 flex flex-col justify-between">
        <div className="flex items-center gap-4 sm:gap-6">
          <div className="h-24 w-24 sm:h-28 sm:w-28 md:h-32 md:w-32 shrink-0">
            <img src="/logo.png" alt="Coke @9" className="h-full w-full object-contain" />
          </div>
          <div className="font-display font-black text-white leading-none tracking-tight text-5xl sm:text-6xl md:text-7xl whitespace-nowrap">
            Coke <span className="text-brand-red">@9/-</span>
          </div>
        </div>

        <div className="my-12">
          <div className="text-[11px] font-bold uppercase tracking-[0.2em] text-brand-red">Operations Control Tower</div>
          <h1 className="font-display text-4xl sm:text-5xl font-black tracking-tight text-white mt-3 leading-tight">
            Every Glass.<br />Every Drop.<br />
            <span className="text-brand-red">Live from Stores.</span>
          </h1>
          <p className="mt-5 text-neutral-400 text-base max-w-md leading-relaxed">
            Real-time tracking of Coca-Cola fountain operations across stores.
          </p>
        </div>

        <div className="hidden md:block text-xs text-neutral-500">
          Strategic Business Partnership: <span className="text-neutral-300 font-semibold">BBIPL</span> · <span className="text-neutral-300 font-semibold">Coca-Cola India</span> · <span className="text-neutral-300 font-semibold">Reliance Retail Ltd.</span>
        </div>
      </div>

      <div className="md:w-1/2 flex items-center justify-center p-6 md:p-14 bg-gray-50">
        <Card className="w-full max-w-md p-8 rounded-2xl border-gray-200 shadow-sm bg-white">
          <div className="text-[11px] font-bold uppercase tracking-[0.2em] text-gray-500">Sign in</div>
          <h2 className="font-display text-2xl font-bold text-gray-900 mt-1">
            {mode === "phone" ? "Phone & password" : "Login ID & password"}
          </h2>

          <div className="mt-4 flex bg-gray-100 rounded-lg p-1" data-testid="login-mode-toggle">
            <button type="button" onClick={() => setMode("id")}
              className={`flex-1 py-2 text-xs font-semibold rounded-md transition ${mode === "id" ? "bg-white text-brand-red shadow-sm" : "text-gray-600"}`}
              data-testid="mode-id-btn">Login ID</button>
            <button type="button" onClick={() => setMode("phone")}
              className={`flex-1 py-2 text-xs font-semibold rounded-md transition ${mode === "phone" ? "bg-white text-brand-red shadow-sm" : "text-gray-600"}`}
              data-testid="mode-phone-btn">Phone</button>
          </div>

          <form onSubmit={submit} className="mt-6 space-y-4">
            {mode === "id" ? (
              <div>
                <label className="text-xs font-semibold text-gray-700">Login ID</label>
                <Input data-testid="login-id-input" className="h-12 text-base mt-1"
                  value={loginId} onChange={(e) => setLoginId(e.target.value.trim())} placeholder="e.g. COKE9-0007 or ADMIN" />
              </div>
            ) : (
              <div>
                <label className="text-xs font-semibold text-gray-700">Phone number</label>
                <div className="mt-1 flex items-center gap-2">
                  <div className="h-12 px-3 flex items-center rounded-lg border border-gray-200 bg-gray-50 text-gray-500 text-sm">+91</div>
                  <Input data-testid="login-phone-input" inputMode="numeric" maxLength={10} className="h-12 text-base"
                    value={phone} onChange={(e) => setPhone(e.target.value.replace(/\D/g, ""))} placeholder="10-digit mobile" />
                </div>
              </div>
            )}
            <div>
              <label className="text-xs font-semibold text-gray-700">Password</label>
              <div className="mt-1 relative">
                <Input data-testid="login-password-input" type={showPw ? "text" : "password"}
                  className="h-12 pr-11 text-base"
                  value={password} onChange={(e) => setPassword(e.target.value)} placeholder="At least 6 characters" />
                <button type="button" onClick={() => setShowPw(v => !v)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-500 hover:text-gray-800"
                  data-testid="toggle-password-visibility">
                  {showPw ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                </button>
              </div>
              <div className="text-[11px] text-gray-500 mt-1">
                Forgot? Ask your Admin/Co-ordinator to reset it from Manage Users.
              </div>
            </div>
            <Button data-testid="login-submit-btn" type="submit" disabled={loading}
              className="w-full h-12 bg-brand-red hover:bg-[#B31721] text-white rounded-xl tap-btn font-semibold">
              Sign in <ArrowRight className="ml-2 h-4 w-4" />
            </Button>
            <div className="text-center text-xs text-gray-500 pt-2">
              Don't have an account?{" "}
              <a href="/signup" data-testid="signup-link" className="font-semibold text-brand-red hover:underline">
                Sign Up
              </a>
            </div>
          </form>
        </Card>
      </div>
    </div>
  );
}
